/**
 * LeanCloud SDK 兼容层
 * 模拟 LeanCloud SDK 接口，底层调用本地 FastAPI 后端
 */

const API_BASE = '/api';

function getToken() {
  return localStorage.getItem('cangjie_token');
}

function setToken(token) {
  localStorage.setItem('cangjie_token', token);
}

function removeToken() {
  localStorage.removeItem('cangjie_token');
}

function getCurrentUserData() {
  const data = localStorage.getItem('cangjie_user');
  return data ? JSON.parse(data) : null;
}

function setCurrentUserData(user) {
  localStorage.setItem('cangjie_user', JSON.stringify(user));
}

function removeCurrentUserData() {
  localStorage.removeItem('cangjie_user');
}

async function request(url, options = {}) {
  const token = getToken();
  const headers = {
    'Content-Type': 'application/json',
    ...(options.headers || {}),
  };
  
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  
  const response = await fetch(`${API_BASE}${url}`, {
    ...options,
    headers,
  });
  
  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: '请求失败' }));
    throw new Error(error.detail || '请求失败');
  }
  
  return response.json();
}

class LCObject {
  constructor(className, attributes = {}) {
    this._className = className;
    this._attributes = { ...attributes };
    this._id = attributes.objectId || null;
  }
  
  get id() {
    return this._id;
  }
  
  get attributes() {
    return { ...this._attributes };
  }
  
  get(key) {
    if (key === 'objectId') return this._id;
    return this._attributes[key];
  }
  
  set(key, value) {
    this._attributes[key] = value;
    return this;
  }
  
  async save() {
    if (this._id) {
      const result = await request(`/objects/${this._className}/${this._id}`, {
        method: 'PUT',
        body: JSON.stringify(this._attributes),
      });
      this._attributes.updatedAt = result.updatedAt;
    } else {
      const result = await request(`/objects/${this._className}`, {
        method: 'POST',
        body: JSON.stringify(this._attributes),
      });
      this._id = result.objectId;
      this._attributes.objectId = result.objectId;
      this._attributes.createdAt = result.createdAt;
    }
    return this;
  }
  
  async destroy() {
    if (!this._id) {
      throw new Error('Cannot destroy an object without id');
    }
    await request(`/objects/${this._className}/${this._id}`, {
      method: 'DELETE',
    });
    return this;
  }
  
  static createWithoutData(className, id) {
    const obj = new LCObject(className);
    obj._id = id;
    return obj;
  }
}

class LCQuery {
  constructor(className) {
    this._className = className;
    this._where = {};
    this._order = null;
    this._limit = null;
    this._skip = null;
  }
  
  equalTo(key, value) {
    this._where[key] = value;
    return this;
  }
  
  contains(key, value) {
    this._where[key] = { $contains: value };
    return this;
  }
  
  descending(key) {
    this._order = `-${key}`;
    return this;
  }
  
  ascending(key) {
    this._order = key;
    return this;
  }
  
  limit(n) {
    this._limit = n;
    return this;
  }
  
  skip(n) {
    this._skip = n;
    return this;
  }
  
  async find() {
    const params = new URLSearchParams();
    
    if (Object.keys(this._where).length > 0) {
      params.set('where', JSON.stringify(this._where));
    }
    if (this._order) {
      params.set('order', this._order);
    }
    if (this._limit) {
      params.set('limit', this._limit);
    }
    if (this._skip) {
      params.set('skip', this._skip);
    }
    
    const queryString = params.toString();
    const url = `/objects/${this._className}${queryString ? `?${queryString}` : ''}`;
    
    const results = await request(url);
    return results.map(item => {
      const obj = new LCObject(this._className, item);
      obj._id = item.objectId;
      return obj;
    });
  }
  
  async first() {
    this._limit = 1;
    const results = await this.find();
    return results[0] || null;
  }
  
  static and(...queries) {
    const combined = new LCQuery(queries[0]._className);
    queries.forEach(q => {
      Object.assign(combined._where, q._where);
    });
    return combined;
  }
}

class LCUser {
  constructor(attributes = {}) {
    this._attributes = { ...attributes };
    this._id = attributes.objectId || null;
  }
  
  get id() {
    return this._id;
  }
  
  get(key) {
    if (key === 'objectId') return this._id;
    return this._attributes[key];
  }
  
  set(key, value) {
    this._attributes[key] = value;
    return this;
  }
  
  setUsername(username) {
    this._attributes.username = username;
    return this;
  }
  
  setPassword(password) {
    this._attributes.password = password;
    return this;
  }
  
  async signUp() {
    const result = await request('/auth/login', {
      method: 'POST',
      body: JSON.stringify({
        username: this._attributes.username,
        password: this._attributes.password,
      }),
    });
    
    setToken(result.token);
    setCurrentUserData(result.user);
    
    this._id = result.user.objectId;
    this._attributes = { ...result.user };
    
    return this;
  }
  
  static async logIn(username, password) {
    const result = await request('/auth/login', {
      method: 'POST',
      body: JSON.stringify({ username, password }),
    });
    
    setToken(result.token);
    setCurrentUserData(result.user);
    
    const user = new LCUser(result.user);
    user._id = result.user.objectId;
    
    return user;
  }
  
  static async logOut() {
    try {
      await request('/auth/logout', { method: 'POST' });
    } catch (e) {
      // ignore
    }
    removeToken();
    removeCurrentUserData();
  }
  
  static current() {
    const userData = getCurrentUserData();
    if (!userData) return null;
    
    const user = new LCUser(userData);
    user._id = userData.objectId;
    return user;
  }
  
  static async switchUser(username) {
    const result = await request('/auth/switch', {
      method: 'POST',
      body: JSON.stringify({ username }),
    });
    
    setToken(result.token);
    setCurrentUserData(result.user);
    
    return new LCUser(result.user);
  }
  
  static async switchBack() {
    const result = await request('/auth/switch-back', {
      method: 'POST',
    });
    
    setToken(result.token);
    setCurrentUserData(result.user);
    
    return new LCUser(result.user);
  }
  
  static async fetchUsers() {
    return request('/users');
  }
}

const LC = {
  Object: LCObject,
  Query: LCQuery,
  User: LCUser,
  
  init() {
    console.log('LC initialized with local backend');
  },
};

export default LC;
export { LCObject, LCQuery, LCUser };
